import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useFeatureRequests, useUserVotes, useToggleVote } from '@/hooks/useFeatureRequests';
import { FeatureRequestCard } from './FeatureRequestCard';
import { SubmitFeatureDialog } from './SubmitFeatureDialog';
import { groupFeatureRequests } from './roadmapGroups';

export function FeatureRequestList() {
  const { t } = useTranslation('roadmap');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [submitOpen, setSubmitOpen] = useState(false);

  const { data: requests, isLoading } = useFeatureRequests(categoryFilter, statusFilter);
  const { data: votedIds = [] } = useUserVotes();
  const toggleVote = useToggleVote();

  const sections = useMemo(() => groupFeatureRequests(requests ?? []), [requests]);
  const filtersActive = categoryFilter !== 'all' || statusFilter !== 'all';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[160px] h-11 sm:h-8 text-xs">
              <SelectValue placeholder={t('features.categoryPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('features.allCategories')}</SelectItem>
              <SelectItem value="feed-management">{t('features.categories.feed_management')}</SelectItem>
              <SelectItem value="optimization">{t('features.categories.optimization')}</SelectItem>
              <SelectItem value="analytics">{t('features.categories.analytics')}</SelectItem>
              <SelectItem value="integrations">{t('features.categories.integrations')}</SelectItem>
              <SelectItem value="ux-ui">{t('features.categories.ux_ui')}</SelectItem>
              <SelectItem value="other">{t('features.categories.other')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px] h-11 sm:h-8 text-xs">
              <SelectValue placeholder={t('features.statusPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('features.allStatuses')}</SelectItem>
              <SelectItem value="under_review">{t('features.statuses.under_review')}</SelectItem>
              <SelectItem value="planned">{t('features.statuses.planned')}</SelectItem>
              <SelectItem value="in_progress">{t('features.statuses.in_progress')}</SelectItem>
              <SelectItem value="completed">{t('features.statuses.completed')}</SelectItem>
              <SelectItem value="declined">{t('features.statuses.declined')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" className="h-11 sm:h-9" onClick={() => setSubmitOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          {t('features.suggestFeature')}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : sections.length === 0 ? (
        filtersActive ? (
          // A7O-12: bei aktivem Filter ist "noch keine Anfragen" falsch.
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-sm">{t('features.emptyFiltered')}</p>
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setCategoryFilter('all');
                setStatusFilter('all');
              }}
            >
              {t('features.resetFilters')}
            </Button>
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-sm">{t('features.empty')}</p>
            <Button variant="link" size="sm" onClick={() => setSubmitOpen(true)}>
              {t('features.suggestLink')}
            </Button>
          </div>
        )
      ) : (
        <div className="space-y-6">
          {sections.map((section) => (
            <section key={section.key} aria-labelledby={`roadmap-section-${section.key}`} className="space-y-2">
              <h2
                id={`roadmap-section-${section.key}`}
                className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
              >
                {t(`features.sections.${section.key}`)} ({section.requests.length})
              </h2>
              {section.requests.map((request) => (
                <FeatureRequestCard
                  key={request.id}
                  request={request}
                  hasVoted={votedIds.includes(request.id)}
                  onToggleVote={(id) => toggleVote.mutate(id)}
                  isVoting={toggleVote.isPending}
                />
              ))}
            </section>
          ))}
        </div>
      )}

      <SubmitFeatureDialog open={submitOpen} onClose={() => setSubmitOpen(false)} />
    </div>
  );
}
